import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  MapPin,
  Phone,
  Star,
  Compass,
  HeartHandshake,
  Lightbulb,
  TrendingUp,
} from "lucide-react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import { fadeUp, stagger } from "@/lib/motion";
import { services } from "@/lib/services";

const SITE_URL = "https://psyloveyourself.com";
const PAGE_URL = `${SITE_URL}/psixotherapeia-patra`;

const steps = [
  {
    Icon: HeartHandshake,
    title: "Γνωριμία & εμπιστοσύνη",
    desc: "Η πρώτη συνεδρία είναι ένας ασφαλής χώρος χωρίς δέσμευση. Μιλάμε για το τι σε φέρνει εδώ και τι θα ήθελες να αλλάξει.",
  },
  {
    Icon: Compass,
    title: "Κοινοί στόχοι",
    desc: "Ορίζουμε μαζί σαφείς, μετρήσιμους στόχους θεραπείας — ώστε να ξέρεις πάντα πού πηγαίνουμε και γιατί.",
  },
  {
    Icon: Lightbulb,
    title: "Πρακτικά εργαλεία CBT",
    desc: "Μαθαίνεις τεχνικές που δουλεύουν ανάμεσα στις συνεδρίες: αναγνώριση σκέψεων, σταδιακή έκθεση, ρύθμιση συναισθήματος.",
  },
  {
    Icon: TrendingUp,
    title: "Μετρήσιμη πρόοδος",
    desc: "Παρακολουθούμε την πορεία σου και προσαρμόζουμε το πλάνο. Στόχος: να γίνεις ο θεραπευτής του εαυτού σου.",
  },
];

const faqs = [
  {
    q: "Πόσο κοστίζει η ψυχοθεραπεία στην Πάτρα;",
    a: "Το κόστος συζητείται στην πρώτη επικοινωνία. Υπάρχει ειδική τιμολόγηση για άνεργους, φοιτητές και ΑμεΑ, ώστε η ψυχοθεραπεία να παραμένει προσιτή.",
  },
  {
    q: "Πόσο διαρκεί μια συνεδρία ψυχοθεραπείας;",
    a: "Κάθε συνεδρία διαρκεί περίπου 50 λεπτά. Στην αρχή οι συνεδρίες γίνονται συνήθως μία φορά την εβδομάδα και αραιώνουν όσο προχωράει η θεραπεία.",
  },
  {
    q: "Τι είναι η Γνωσιακή Συμπεριφορική Ψυχοθεραπεία (CBT);",
    a: "Η CBT είναι η πιο ερευνητικά τεκμηριωμένη μορφή ψυχοθεραπείας. Εστιάζει στη σχέση σκέψεων, συναισθημάτων και συμπεριφοράς και σου δίνει πρακτικά εργαλεία για άγχος, OCD, κατάθλιψη, φοβίες και χαμηλή αυτοεκτίμηση.",
  },
  {
    q: "Μπορώ να κάνω ψυχοθεραπεία online;",
    a: "Ναι. Οι online συνεδρίες γίνονται μέσω ασφαλούς βιντεοκλήσης με πλήρη εμπιστευτικότητα και έχουν αποδεδειγμένα ισάξια αποτελεσματικότητα με τις δια ζώσης.",
  },
  {
    q: "Πού βρίσκεται το γραφείο στην Πάτρα;",
    a: "Στη Ρήγα Φεραίου 119, στο κέντρο της Πάτρας — σε εύκολα προσβάσιμο σημείο, κοντά σε στάσεις και με parking στην περιοχή.",
  },
];

export default function PsixotherapeiaPatra() {
  const schema = {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "MedicalBusiness"],
    name: "Ψυχοθεραπεία στην Πάτρα · Άννα Παπαχρονόπουλου",
    url: PAGE_URL,
    image:
      "https://customer-assets.emergentagent.com/job_therapy-anna-gr/artifacts/nx9midrs_IMG_7568.jpeg",
    telephone: "+30-697-8042360",
    email: "anniepapach@gmail.com",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Ρήγα Φεραίου 119",
      addressLocality: "Πάτρα",
      addressRegion: "Αχαΐα",
      postalCode: "26221",
      addressCountry: "GR",
    },
    geo: { "@type": "GeoCoordinates", latitude: 38.246639, longitude: 21.734573 },
    areaServed: { "@type": "City", name: "Πάτρα" },
    medicalSpecialty: "Psychiatric",
    priceRange: "€€",
    inLanguage: "el-GR",
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Αρχική", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "Ψυχοθεραπεία Πάτρα", item: PAGE_URL },
    ],
  };

  return (
    <>
      <Helmet>
        <html lang="el-GR" />
        <title>{"Ψυχοθεραπεία Πάτρα · Γνωσιακή Συμπεριφορική (CBT) | Άννα Παπαχρονόπουλου"}</title>
        <meta
          name="description"
          content="Ψυχοθεραπεία στην Πάτρα με Γνωσιακή Συμπεριφορική Θεραπεία (CBT). Ατομικές συνεδρίες για άγχος, OCD, κατάθλιψη, αυτοεκτίμηση — δια ζώσης στη Ρήγα Φεραίου 119 ή online. Κλείσε ραντεβού online."
        />
        <meta
          name="keywords"
          content="ψυχοθεραπεία Πάτρα, γνωσιακή συμπεριφορική ψυχοθεραπεία Πάτρα, γνωσιακή συμπεριφορική θεραπεία Πάτρα, CBT Πάτρα, ψυχοθεραπευτής Πάτρα, ατομική ψυχοθεραπεία Πάτρα, online ψυχοθεραπεία"
        />
        <link rel="canonical" href={PAGE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Ψυχοθεραπεία Πάτρα · Γνωσιακή Συμπεριφορική (CBT)" />
        <meta
          property="og:description"
          content="Ατομική ψυχοθεραπεία CBT στην Πάτρα και online — άγχος, OCD, κατάθλιψη, αυτοεκτίμηση. Κλείσε ραντεβού online."
        />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:locale" content="el_GR" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbs)}</script>
      </Helmet>

      <Header />

      <main className="bg-background pt-32 pb-24" data-testid="psixotherapeia-page">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-12 lg:px-20">
          {/* Hero */}
          <motion.div {...fadeUp}>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-body text-muted-foreground hover:text-foreground transition-colors mb-8"
            >
              <ArrowLeft size={14} strokeWidth={1.5} />
              Αρχική
            </Link>
            <p className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card border border-border/60 text-[11px] tracking-[0.22em] uppercase font-body font-medium text-foreground/70 mb-6">
              <MapPin size={14} strokeWidth={1.5} className="text-primary" />
              Πάτρα · Δια ζώσης & Online
            </p>
            <h1 className="font-heading font-medium tracking-tight text-foreground text-4xl sm:text-5xl lg:text-[72px] leading-[1.04]">
              Ψυχοθεραπεία <span className="italic font-light">στην Πάτρα.</span>
              <br />
              <span className="text-foreground/85">
                Με μέθοδο που <span className="italic font-light">αποδίδει.</span>
              </span>
            </h1>
            <p className="mt-7 font-body font-light text-lg sm:text-xl text-foreground/75 leading-relaxed max-w-[680px]">
              Η <strong className="text-foreground font-normal">Γνωσιακή Συμπεριφορική Ψυχοθεραπεία (CBT)</strong>{" "}
              είναι η πιο ερευνητικά τεκμηριωμένη προσέγγιση διεθνώς. Στο γραφείο
              μου στο κέντρο της Πάτρας — ή online — δουλεύουμε μαζί με σαφείς
              στόχους και πρακτικά εργαλεία, όχι ατέρμονες συζητήσεις.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link
                to="/rantevou"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-foreground text-background px-8 py-4 font-body text-base font-medium hover:-translate-y-0.5 transition-all duration-300"
                data-testid="therapy-hero-cta"
              >
                Κλείσε Ραντεβού Online
                <ArrowRight size={18} strokeWidth={1.5} />
              </Link>
              <a
                href="tel:+306978042360"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-border bg-transparent px-8 py-4 font-body text-base font-medium text-foreground hover:bg-card transition-all"
              >
                <Phone size={16} strokeWidth={1.5} />
                697 8042 360
              </a>
            </div>
          </motion.div>

          {/* How it works */}
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="mt-24">
            <p className="text-[11px] tracking-[0.25em] uppercase font-body font-medium text-primary mb-4">
              Πώς δουλεύουμε
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl text-foreground tracking-tight max-w-[560px]">
              Η πορεία της ψυχοθεραπείας, βήμα-βήμα
            </h2>
          </motion.div>
          <motion.div {...stagger(0.08)} className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
            {steps.map((s, i) => (
              <motion.div
                key={s.title}
                variants={{
                  initial: { opacity: 0, y: 24 },
                  whileInView: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
                  },
                }}
                className="bg-card border border-border/50 rounded-3xl p-7 sm:p-8"
                data-testid={`therapy-step-${i + 1}`}
              >
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                    <s.Icon size={18} strokeWidth={1.7} />
                  </div>
                  <span className="font-heading italic text-foreground/40 text-2xl">0{i + 1}</span>
                </div>
                <h3 className="font-heading text-2xl text-foreground tracking-tight">{s.title}</h3>
                <p className="mt-3 font-body font-light text-foreground/70 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* What it helps with */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.15 }}
            className="mt-20 p-8 sm:p-12 bg-card rounded-3xl border border-border/50"
          >
            <p className="text-[11px] tracking-[0.25em] uppercase font-body font-medium text-primary mb-4">
              Πού βοηθά
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl text-foreground tracking-tight">
              Σε τι βοηθά η ψυχοθεραπεία CBT
            </h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              {services.map((s) => (
                <Link
                  key={s.slug}
                  to={`/ipiresies/${s.slug}`}
                  className="group flex items-start justify-between gap-4 bg-background border border-border/60 rounded-2xl p-6 hover:bg-card transition-all duration-500"
                >
                  <span className="font-heading text-xl text-foreground tracking-tight">
                    {s.h1.replace(/ στην Πάτρα| στην Ελλάδα/g, "").replace(/^Ψυχολόγος για |^Ειδικός για /, "")}
                  </span>
                  <ArrowRight
                    size={16}
                    strokeWidth={1.5}
                    className="text-foreground/60 mt-1 transition-transform group-hover:translate-x-0.5"
                  />
                </Link>
              ))}
            </div>
          </motion.div>

          {/* Nearby areas */}
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.18 }} className="mt-14 flex flex-wrap gap-3">
            {[
              { to: "/psixologos-patra", label: "Ψυχολόγος στην Πάτρα" },
              { to: "/psixologos-rio", label: "Ψυχοθεραπεία για το Ρίο" },
              { to: "/psixologos-aigio", label: "Ψυχοθεραπεία για το Αίγιο" },
              { to: "/psixologos-foitites-patras", label: "Για φοιτητές Παν. Πατρών" },
            ].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="inline-flex items-center gap-2 text-sm font-body text-foreground/75 hover:text-foreground bg-card border border-border/60 rounded-full px-5 py-2.5 transition-colors"
                data-testid={`nearby-link-${l.to.slice(1)}`}
              >
                {l.label}
                <ArrowRight size={13} strokeWidth={1.5} />
              </Link>
            ))}
          </motion.div>

          {/* FAQ */}
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }} className="mt-20">
            <p className="text-[11px] tracking-[0.25em] uppercase font-body font-medium text-primary mb-4">
              Συχνές ερωτήσεις
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl text-foreground tracking-tight">
              Ό,τι θες να ξέρεις για την ψυχοθεραπεία στην Πάτρα
            </h2>
            <div className="mt-8 space-y-4">
              {faqs.map((f, i) => (
                <div
                  key={f.q}
                  className="bg-card border border-border/50 rounded-2xl p-6 sm:p-7"
                  data-testid={`therapy-faq-${i + 1}`}
                >
                  <h3 className="font-heading text-xl text-foreground tracking-tight">{f.q}</h3>
                  <p className="mt-2.5 font-body font-light text-foreground/70 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Final CTA */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.25 }}
            className="mt-20 p-10 sm:p-14 bg-foreground text-background rounded-3xl"
          >
            <div className="flex items-center gap-2 mb-5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={14} fill="currentColor" />
              ))}
            </div>
            <h2 className="font-heading text-3xl sm:text-5xl tracking-tight leading-[1.05]">
              Το πρώτο βήμα είναι
              <br />
              <span className="italic font-light">πάντα το πιο δύσκολο.</span>
            </h2>
            <p className="mt-5 font-body font-light text-background/85 leading-relaxed max-w-[520px]">
              Διάλεξε ημέρα και ώρα που σε βολεύει — δια ζώσης στην Πάτρα ή
              online. Χωρίς δέσμευση, με πλήρη εμπιστευτικότητα.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link
                to="/rantevou"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-background text-foreground px-7 py-3.5 font-body text-sm font-medium hover:-translate-y-0.5 transition-all"
                data-testid="therapy-final-cta"
              >
                Κλείσε Ραντεβού Online
                <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
              <a
                href="tel:+306978042360"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-background/30 px-7 py-3.5 font-body text-sm font-medium text-background hover:bg-background/10 transition-all"
              >
                <Phone size={14} strokeWidth={1.5} />
                697 8042 360
              </a>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </>
  );
}
